import type { OwnedDmcLink } from '@/app/api/lib/dmcs/owned-dmc-links';
import type { Role } from '@/app/api/lib/permissions/matrix';
import type { NavItem } from '@/configurations/tracking/navigation';

type NavLinkLabelKey = 'home' | 'myProjects' | 'myCatalog';

export type NavLink = {
    /** Clé i18n `nav.*`, traduite au rendu quand `label` est absent. */
    labelKey: NavLinkLabelKey;
    href: string;
    /**
     * Libellé déjà résolu, qui remplace la traduction de `labelKey` : le nom de la DMC quand le
     * viewer en possède plusieurs. Un nom propre n'a pas à passer par l'i18n.
     */
    label?: string;
};

export type NavLinksConfig = {
    /** Links rendered before the Destinations button. */
    before: readonly NavLink[];
    /** Links rendered after the Destinations button. */
    after: readonly NavLink[];
};

/**
 * Navbar links per rôle — order: before-Destinations · Destinations · after-Destinations.
 *   - Agency  → Accueil | Destinations | Mes projets
 *   - DMC     → Destinations | son/ses catalogue(s). Home et My Projects sont masqués : Home
 *               rend la homepage Visiteur, My Projects est Agency-only.
 *               Une seule fiche → « Mon catalogue » traduit, vers `/dmcs/{slug}`.
 *               Plusieurs fiches → un lien par fiche, nommé par la DMC : le viewer est owner de
 *               chacune, un lien unique en rendrait les autres inatteignables depuis la barre.
 *               Aucune fiche résolvable → aucun lien (défense-en-profondeur).
 *   - Visitor → Accueil | Destinations
 */
export function visibleNavLinks(
    role: Role | undefined,
    dmcLinks: readonly OwnedDmcLink[],
): NavLinksConfig {
    if (role === 'dmc') {
        return { before: [], after: catalogLinks(dmcLinks) };
    }
    if (role === 'agency') {
        return {
            before: [{ labelKey: 'home', href: '/' }],
            after: [{ labelKey: 'myProjects', href: '/my-projects' }],
        };
    }
    return {
        before: [{ labelKey: 'home', href: '/' }],
        after: [],
    };
}

/**
 * Replie les liens catalogue d'un DMC multi-fiches en un groupe destiné à un menu déroulant
 * « Mon catalogue » unique ; en dessous de deux fiches, rien à replier et les liens passent tels
 * quels. Pure : le rendu (barre desktop vs drawer mobile) décide quoi faire du groupe.
 */
export function splitCatalogMenu(links: readonly NavLink[]): {
    links: readonly NavLink[];
    catalogMenu: readonly NavLink[];
} {
    const catalogMenu = links.filter((link) => link.labelKey === 'myCatalog');

    if (catalogMenu.length < 2) return { links, catalogMenu: [] };

    return { links: links.filter((link) => link.labelKey !== 'myCatalog'), catalogMenu };
}

const NAV_ITEMS = {
    home: 'home_link',
    myProjects: 'my_projects',
    myCatalog: 'my_catalog',
} as const satisfies Record<NavLinkLabelKey, NavItem>;

export function navItemFor(labelKey: NavLinkLabelKey): (typeof NAV_ITEMS)[NavLinkLabelKey] {
    return NAV_ITEMS[labelKey];
}

function catalogLinks(dmcLinks: readonly OwnedDmcLink[]): readonly NavLink[] {
    const isSingle = dmcLinks.length === 1;

    return dmcLinks.map((dmc) => ({
        labelKey: 'myCatalog',
        href: dmc.catalogHref,
        // Sans nom (fiche non onboardée dont l'instance n'a pas répondu), on garde la traduction :
        // le lien reste nommé et cliquable, il perd juste sa précision.
        ...(isSingle || !dmc.name ? {} : { label: dmc.name }),
    }));
}
